import {
  HHChip,
  HHChipProps,
  HHCollapsibleTable,
  HHError,
  HHIconButton,
  HHStack,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import { Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { MouseEvent, useEffect, useState } from 'react';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import { formatCurrency } from '../../utils/currency-helpers';
import { formatDate } from '../../utils/date-helper';
import { CalculationStep, Operator } from './calculation-step';
import { ProgramCapForm } from './form/form';
import { ProgramCapRow, useProgramCap } from './hooks/program-cap-hook';
import {
  LockPriceCorrectionIcon,
  LockPriceCorrectionProps,
  LockPriceCorrectionSummary,
} from './lock-price-correction-indicator';
import { ProgramCapCredits } from './program-cap-credits';
import { ProgramCapGridFooter } from './program-cap-grid-footer';

const getLockPriceCorrection = (
  row: ProgramCapRow,
): LockPriceCorrectionProps | null => {
  if (row.lockPrice == null || row.correctedUserBasedLockPrice == null) {
    return null;
  }

  return {
    lockPrice: row.lockPrice,
    correctedUserBasedLockPrice: row.correctedUserBasedLockPrice,
  };
};

const getRefundAmountDisplay = (row: ProgramCapRow): JSX.Element => {
  const amount = row.lockPrice ?? row.totalRefundAmount;
  const lockPriceCorrection = getLockPriceCorrection(row);

  return (
    <HHStack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="center"
    >
      <HHTypography hhVariant="body2" layoutStyles={{ lineHeight: 1 }}>
        {formatCurrency(amount, 'USD')}
      </HHTypography>
      {lockPriceCorrection && (
        <LockPriceCorrectionIcon {...lockPriceCorrection} />
      )}
    </HHStack>
  );
};

// ACCOUNTS-AND-PLAN-MIGRATION: row.clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
const ProgramCapRowDetails = ({ row }: { row: ProgramCapRow }): JSX.Element => {
  const lockPriceCorrection = getLockPriceCorrection(row);

  return (
    <HHStack>
      {lockPriceCorrection && (
        <LockPriceCorrectionSummary {...lockPriceCorrection} />
      )}
      <ProgramCapCredits
        clientId={row.clientId}
        inquiryDate={row.endDate ?? ''}
        isDisabled={!!row.lockDate}
      />
    </HHStack>
  );
};

const getStatusChip = (
  lockDate: string | null,
  startDate: string,
): JSX.Element => {
  const now = new Date();
  const start = new Date(startDate);

  let label = 'Active';
  let color: HHChipProps['color'] = 'primary';

  if (lockDate) {
    label = 'Locked';
    color = 'secondary';
  } else if (start > now) {
    label = 'Future';
    color = 'info';
  }

  return <HHChip label={label} color={color} size="small" hhVariant="filled" />;
};

const CalculationSkeleton = (): JSX.Element => (
  <HHStack width="100%" spacing={4}>
    <HHStack
      direction="row"
      alignItems="center"
      width="100%"
      justifyContent="space-between"
    >
      <HHStack alignItems="center" spacing={1}>
        <Skeleton variant="text" width={60} height={20} />
        <Skeleton variant="text" width={80} height={16} />
      </HHStack>
      <HHStack px={2}>
        <Skeleton variant="text" width={10} height={20} />
      </HHStack>
      <HHStack alignItems="center" spacing={1}>
        <Skeleton variant="text" width={30} height={20} />
        <Skeleton variant="text" width={60} height={16} />
      </HHStack>
      <HHStack px={2}>
        <Skeleton variant="text" width={10} height={20} />
      </HHStack>
      <HHStack alignItems="center" spacing={1}>
        <Skeleton variant="text" width={60} height={20} />
        <Skeleton variant="text" width={80} height={16} />
      </HHStack>
      <HHStack px={2}>
        <Skeleton variant="text" width={10} height={20} />
      </HHStack>
      <HHStack alignItems="center" spacing={1}>
        <Skeleton variant="text" width={50} height={20} />
        <Skeleton variant="text" width={30} height={16} />
      </HHStack>
      <HHStack px={2}>
        <Skeleton variant="text" width={10} height={20} />
      </HHStack>
      <HHStack alignItems="center" spacing={1}>
        <Skeleton variant="text" width={60} height={20} />
        <Skeleton variant="text" width={80} height={16} />
      </HHStack>
    </HHStack>
  </HHStack>
);

const RefundAmountSkeleton = (): JSX.Element => (
  <HHStack spacing={1} alignItems="center">
    <Skeleton variant="text" width={80} height={20} />
    <Skeleton variant="rectangular" width={60} height={24} />
  </HHStack>
);

const ProgramCapTableSkeleton = (): JSX.Element => (
  <HHCollapsibleTable
    headers={[
      { align: 'left', headerTitle: 'Client Name' },
      { align: 'left', headerTitle: 'Calculation' },
      { align: 'left', headerTitle: 'Total Refund Amount' },
      { align: 'left', headerTitle: 'Effective Dates' },
      { align: 'left', headerTitle: 'Lock Date' },
      { align: 'left', headerTitle: 'Actions' },
      { align: 'left', headerTitle: 'Status' },
    ]}
    rows={Array.from({ length: 3 }, (_, index) => ({
      rowId: `skeleton-${index}`,
      open: false,
      setOpen: (): void => {
        // No-op for skeleton rows
      },
      collapsibleRowContent: <div />,
      columnValues: [
        { columnItem: <Skeleton variant="text" width={120} height={20} /> },
        { columnItem: <CalculationSkeleton /> },
        { columnItem: <RefundAmountSkeleton /> },
        { columnItem: <Skeleton variant="text" width={100} height={20} /> },
        { columnItem: <Skeleton variant="text" width={80} height={20} /> },
        { columnItem: <Skeleton variant="circular" width={24} height={24} /> },
        {
          columnItem: <Skeleton variant="rectangular" width={60} height={24} />,
        },
      ],
    }))}
    emptyRowContent={<div />}
  />
);

export interface ProgramCapGridProps {
  clientId: number | null;
}

interface CollapsibleTableRow {
  rowId: string;
  open: boolean;
  setOpen: (event: MouseEvent<HTMLButtonElement>) => void;
  collapsibleRowContent: JSX.Element;
  columnValues: Array<{ columnItem: JSX.Element | string }>;
}

// ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
export const ProgramCapGrid = ({
  clientId,
}: ProgramCapGridProps): JSX.Element => {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const {
    rows,
    loading,
    error,
    handleEdit,
    handleReport,
    generateReportLoading,
    generateReportError,
    updateProgramCapError,
    updateProgramCapSuccess,
    downloadReportError,
    currentPage,
    totalItems,
    pageSize,
    setCurrentPage,
    setPageSize,
  } = useProgramCap(clientId, getStatusChip, onClose);

  useEffect(() => {
    if (updateProgramCapSuccess) {
      setSnackbarState('Program cap updated successfully', 'success', 3000);
    }
  }, [updateProgramCapSuccess, setSnackbarState]);

  useEffect(() => {
    if (updateProgramCapError) {
      setSnackbarState('Error updating program cap', 'error', 3000);
    }
  }, [updateProgramCapError, setSnackbarState]);

  useEffect(() => {
    if (generateReportError || downloadReportError) {
      setSnackbarState('Error generating report', 'error', 3000);
    }
  }, [generateReportError, downloadReportError, setSnackbarState]);

  if (loading) {
    return (
      <HHStack
        flex={1}
        direction="column"
        justifyContent="space-between"
        minHeight="0"
      >
        <ProgramCapTableSkeleton />
        <HHStack />
      </HHStack>
    );
  }

  if (error && !error.message?.includes('status code 404')) {
    return (
      <HHError body="Error fetching program caps" hhVariant="variant-bypass" />
    );
  }

  if (rows.length === 0) {
    return (
      <HHStack alignItems="center" justifyContent="center" flex={1}>
        <HHTypography hhVariant="body1">No program caps found</HHTypography>
      </HHStack>
    );
  }

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
    setExpandedRowId(null); // Close any expanded rows when changing pages
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    setExpandedRowId(null); // Close any expanded rows when changing page size
    // The hook will automatically refetch when page/pageSize change
  };

  return (
    <HHStack
      flex={1}
      direction="column"
      justifyContent="space-between"
      minHeight="0"
    >
      <HHCollapsibleTable
        headers={[
          { align: 'left', headerTitle: 'Client Name' },
          { align: 'left', headerTitle: 'Calculation' },
          { align: 'left', headerTitle: 'Total Refund Amount' },
          { align: 'left', headerTitle: 'Effective Dates' },
          { align: 'left', headerTitle: 'Lock Date' },
          { align: 'left', headerTitle: 'Actions' },
          { align: 'left', headerTitle: 'Status' },
        ]}
        rows={rows.map(
          (row): CollapsibleTableRow => ({
            rowId: row.id,
            open: expandedRowId === row.id,
            setOpen: (_event: MouseEvent<HTMLButtonElement>): void => {
              setExpandedRowId(expandedRowId === row.id ? null : row.id);
            },
            collapsibleRowContent: <ProgramCapRowDetails row={row} />,
            columnValues: [
              { columnItem: row.clientName || 'Unknown Client' },
              {
                columnItem: row.loading ? (
                  <CalculationSkeleton />
                ) : (
                  <HHStack width="100%" spacing={4}>
                    <HHStack
                      direction="row"
                      alignItems="center"
                      width="100%"
                      justifyContent="space-between"
                    >
                      <CalculationStep
                        value={formatCurrency(row.sumAmount, 'USD')}
                        description="Total Submitted"
                      />
                      <Operator symbol="/" />
                      <CalculationStep
                        value={row.numMembers.toString()}
                        description="Members"
                      />
                      <Operator symbol="=" />
                      <CalculationStep
                        value={formatCurrency(row.averageStickerPrice, 'USD')}
                        description="Average Sticker Price"
                      />
                      <Operator symbol="-" />
                      <CalculationStep
                        value={formatCurrency(row.cap, 'USD')}
                        description="Cap"
                      />
                      <Operator symbol="=" />
                      <CalculationStep
                        value={formatCurrency(row.refundPerMember, 'USD')}
                        description="Refund Per Member"
                      />
                    </HHStack>
                  </HHStack>
                ),
              },
              {
                columnItem: row.loading ? (
                  <RefundAmountSkeleton />
                ) : (
                  getRefundAmountDisplay(row)
                ),
              },
              { columnItem: row.effectiveDates },
              { columnItem: row.lockDate ? formatDate(row.lockDate) : '--' },
              {
                columnItem: (
                  <HHStack direction="row" spacing={1}>
                    {!row.lockDate && canWrite && (
                      <HHTooltip title="Edit" hhVariant="variant-bypass">
                        <HHIconButton
                          hhVariant="variant-bypass"
                          size="small"
                          onClick={(): void => {
                            setDrawerContent(
                              <ProgramCapForm
                                onSubmit={handleEdit}
                                initialValues={{
                                  programCapId: row.programCapId,
                                  clientId: row.clientId,
                                  startDate: dayjs(row.startDate).toDate(),
                                  endDate: dayjs(row.endDate).toDate(),
                                  cap: row.cap,
                                }}
                              />,
                              <HHTypography hhVariant="h6">
                                Edit Program Cap
                              </HHTypography>,
                              undefined,
                              'temporary',
                            );
                          }}
                          aria-label="Edit Program Cap"
                        >
                          <EditIcon />
                        </HHIconButton>
                      </HHTooltip>
                    )}
                    {row.lockDate && (
                      <HHTooltip
                        title={
                          generateReportLoading
                            ? 'Processing...'
                            : 'Download Report'
                        }
                        hhVariant="variant-bypass"
                      >
                        <HHIconButton
                          hhVariant="variant-bypass"
                          size="small"
                          onClick={(): void => {
                            if (!generateReportLoading) {
                              void handleReport(row);
                            }
                          }}
                          aria-label="Download Program Cap Report"
                        >
                          <DownloadIcon />
                        </HHIconButton>
                      </HHTooltip>
                    )}
                  </HHStack>
                ),
              },
              { columnItem: row.status },
            ],
          }),
        )}
        emptyRowContent={
          <HHStack direction="row" padding={4} justifyContent="center">
            <HHTypography hhVariant="muted">No program caps exist</HHTypography>
          </HHStack>
        }
      />

      <ProgramCapGridFooter
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </HHStack>
  );
};
